import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";

const SITE = "https://anabiyadecor.com";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SeoVerification — fetches search-engine verification tokens from the backend
 * (`/api/seo/meta`, driven by GOOGLE_SITE_VERIFICATION / BING_SITE_VERIFICATION
 * env vars) and injects the meta tags into <head>. Mount once at the app root
 * so you can rotate tokens without a code redeploy.
 */
export const SeoVerification = () => {
  const [tokens, setTokens] = useState({ google: "", bing: "" });

  useEffect(() => {
    axios
      .get(`${API}/seo/meta`)
      .then(({ data }) =>
        setTokens({
          google: data.google_site_verification || "",
          bing: data.bing_site_verification || "",
        })
      )
      .catch(() => {});
  }, []);

  if (!tokens.google && !tokens.bing) return null;
  return (
    <Helmet>
      {tokens.google && <meta name="google-site-verification" content={tokens.google} />}
      {tokens.bing && <meta name="msvalidate.01" content={tokens.bing} />}
    </Helmet>
  );
};

/**
 * Seo — per-page SEO meta manager. Injects <title>, description, canonical,
 * OG + Twitter tags and optional JSON-LD structured data via react-helmet-async.
 */
export const Seo = ({
  title,
  description,
  path = "/",
  image,
  type = "website",
  jsonLd,
  noindex = false,
}) => {
  const url = `${SITE}${path.startsWith("/") ? path : `/${path}`}`;
  const ogImage = image || `${SITE}/og-image.jpg`;
  const fullTitle = title
    ? (/anabiya decor/i.test(title) ? title : `${title} — Anabiya Decor`)
    : "Anabiya Decor | Premium Bedding & Home Décor";
  const desc = description ||
    "Anabiya Decor offers premium bedding, bedsheets, comforters, quilts, curtains and hotel linens, crafted in Mumbai for retailers, designers and hospitality buyers worldwide.";
  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, follow" />}

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Anabiya Decor" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
};

export const productJsonLd = (product, format) => ({
  "@context": "https://schema.org/",
  "@type": "Product",
  name: product.title,
  description: product.description || product.subtitle || product.title,
  image: (product.images || []).map((i) => (typeof i === "string" ? i : i?.url)).filter(Boolean),
  sku: product.slug,
  brand: { "@type": "Brand", name: "Anabiya Decor" },
  category: product.category,
  offers: {
    "@type": "Offer",
    priceCurrency: "USD",
    price: Number(product.price || 0),
    availability:
      typeof product.stock === "number" && product.stock <= 0
        ? "https://schema.org/OutOfStock"
        : "https://schema.org/InStock",
    url: `${SITE}/product/${product.slug}`,
    seller: { "@type": "Organization", name: "Anabiya Decor" },
  },
});
